import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';

// Validate required environment variables
// Check if we're in development mode
const isDevelopment = import.meta.env.DEV;

// Get environment variable with fallback handling
const getEnvVar = (key: string) => {
  const value = import.meta.env[key];
  
  // If value exists, return it
  if (value) {
    return value;
  }
  
  // In development, use mock values if env vars are missing
  if (isDevelopment) {
    console.warn(`Missing ${key}, using mock value for development`);
    return 'mock-value';
  }
  
  // In production, warn about missing env vars but provide a fallback
  console.error(`Missing environment variable: ${key}`);
  return 'missing-env-var';
};

const firebaseConfig = {
  apiKey: getEnvVar('VITE_FIREBASE_API_KEY'),
  authDomain: getEnvVar('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: getEnvVar('VITE_FIREBASE_PROJECT_ID'),
  storageBucket: getEnvVar('VITE_FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: getEnvVar('VITE_FIREBASE_MESSAGING_SENDER_ID'),
  appId: getEnvVar('VITE_FIREBASE_APP_ID')
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Initialize persistence without top-level await
const initializeFirebase = () => {
  // Set auth persistence
  setPersistence(auth, browserLocalPersistence)
    .then(() => {
      console.log('Auth persistence set to LOCAL');
      
      // Try to enable Firestore persistence
      return enableMultiTabIndexedDbPersistence(db)
        .then(() => {
          console.log('Firestore persistence enabled');
        })
        .catch((err) => {
          if (err.code === 'failed-precondition') {
            console.warn('Multiple tabs open, persistence enabled in first tab only');
          } else if (err.code === 'unimplemented') {
            console.warn('Browser doesn\'t support persistence');
          } else {
            console.error('Error enabling Firestore persistence:', err);
          }
        });
    })
    .catch((err) => {
      console.error('Error initializing Firebase:', err);
    });
};

// Initialize Firebase without waiting
initializeFirebase();

// Helper to check if Firebase is properly configured
export const isFirebaseConfigured = () => {
  return Boolean(
    firebaseConfig.apiKey && 
    firebaseConfig.apiKey !== 'missing-env-var' &&
    firebaseConfig.apiKey !== 'mock-value' &&
    firebaseConfig.authDomain &&
    firebaseConfig.authDomain !== 'missing-env-var' &&
    firebaseConfig.authDomain !== 'mock-value' &&
    firebaseConfig.projectId
  );
};

export { app, auth, db };